// ─────────────────────────────────────────────────────────────────────────────
// Crew — activity suggestions + the booking-intent funnel (Agent B).
//
// Order: the feed renders activities in the EXACT order the server returns them
// (no client re-sort). Prices: priceFromPence is integer pence → formatPrice.
// Offers are labelled HONESTLY (OfferTag): affiliate / card-linked / CLO-eligible.
//
// ANALYTICS — IMPORTANT: `activity_viewed` is the ONLY funnel event the client
// fires anywhere in this app. Every other funnel event is emitted server-side by
// the mutation it belongs to. We fire it once per activity, when it first becomes
// visible, via IntersectionObserver (consent-gated inside track()).
//
// Intent flow: Shortlist → createIntent(status 'shortlisted'). Then "We're in" →
// patchIntent(status 'committed', estimatedHeads). Then "Book / view" →
// attributionClick (records click_out server-side) → navigate to the returned
// redirect URL. createIntent/patchIntent emit intent_shortlisted/intent_committed
// server-side; click_out too — the client adds none of these.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Activity, BookingIntent, Group, Member } from '../lib/types';
import { api, ApiError } from '../lib/api';
import { track } from '../lib/analytics';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { ErrorBanner, EmptyState, Spinner } from '../components/Feedback';
import { OfferTag } from '../components/OfferTag';
import { errorMessage, formatPrice } from '../lib/format';

interface ActivitiesProps {
  group: Group;
  me: Member;
  goalId?: string;
  memberCount: number;
  intent: BookingIntent | null;
  onIntent: (intent: BookingIntent | null) => void;
}

export function Activities({ group, me, goalId, memberCount, intent, onIntent }: ActivitiesProps) {
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  // per-activity busy flags so each card's button shows its own spinner
  const [busyId, setBusyId] = useState<string | null>(null);
  // estimated heads default to the crew size, but the user can adjust on commit
  const [heads, setHeads] = useState(memberCount);

  // ── Load feed (preserve server order) ───────────────────────────────────────
  useEffect(() => {
    let alive = true;
    setActivities(null);
    setError(null);
    api
      .suggestActivities({ limit: 20 })
      .then((res) => alive && setActivities(res.activities))
      .catch((err: unknown) => alive && setError(errorMessage(err, 'Could not load activities. Please try again.')));
    return () => {
      alive = false;
    };
  }, [retryKey]);

  const shortlistedId = intent?.status === 'shortlisted' || intent?.status === 'committed' ? intent.activityId : null;

  async function shortlist(a: Activity) {
    setBusyId(a.id);
    setError(null);
    try {
      const res = await api.createIntent(group.id, {
        activityId: a.id,
        goalId,
        status: 'shortlisted',
        estimatedHeads: memberCount,
        participantIds: [me.id],
      });
      onIntent(res.intent);
    } catch (err) {
      setError(errorMessage(err, 'Could not shortlist that activity. Please try again.'));
    } finally {
      setBusyId(null);
    }
  }

  async function commit(a: Activity) {
    if (!intent) return;
    setBusyId(a.id);
    setError(null);
    try {
      const res = await api.patchIntent(intent.id, {
        status: 'committed',
        estimatedHeads: Math.max(1, heads),
      });
      onIntent(res.intent);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('This plan was just updated. Reload and confirm again.');
      } else {
        setError(errorMessage(err, 'Could not confirm. Please try again.'));
      }
    } finally {
      setBusyId(null);
    }
  }

  async function clickOut(a: Activity) {
    setBusyId(a.id);
    setError(null);
    try {
      const res = await api.attributionClick({
        activityId: a.id,
        groupId: group.id,
        intentId: intent?.id,
        url: a.url,
      });
      // Server recorded click_out; send the user onward to the merchant.
      window.open(res.redirect, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(errorMessage(err, 'Could not open that link. Please try again.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">Activities</h1>
        <p className="mt-1 text-sm text-ink-700/70">
          Ideas for your crew. Shortlist one, confirm who’s in, then book.
        </p>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => setRetryKey((k) => k + 1)} />}

      {activities === null && !error && <Spinner label="Finding activities…" />}

      {activities !== null && activities.length === 0 && (
        <EmptyState title="No activities yet">
          Check back soon — we’re curating ideas for your crew.
        </EmptyState>
      )}

      {activities !== null && activities.length > 0 && (
        <ul className="flex flex-col gap-4">
          {activities.map((a) => (
            <ActivityCard
              key={a.id}
              activity={a}
              groupId={group.id}
              memberId={me.id}
              shortlisted={shortlistedId === a.id}
              committed={intent?.status === 'committed' && intent.activityId === a.id}
              heads={heads}
              onHeads={setHeads}
              busy={busyId === a.id}
              anyBusy={busyId !== null}
              onShortlist={() => shortlist(a)}
              onCommit={() => commit(a)}
              onClickOut={() => clickOut(a)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── A single activity card: fires activity_viewed once when first seen ─────────
interface ActivityCardProps {
  activity: Activity;
  groupId: string;
  memberId: string;
  shortlisted: boolean;
  committed: boolean;
  heads: number;
  onHeads: (n: number) => void;
  busy: boolean;
  anyBusy: boolean;
  onShortlist: () => void;
  onCommit: () => void;
  onClickOut: () => void;
}

function ActivityCard({
  activity, groupId, memberId, shortlisted, committed, heads, onHeads,
  busy, anyBusy, onShortlist, onCommit, onClickOut,
}: ActivityCardProps) {
  const ref = useRef<HTMLLIElement | null>(null);
  const viewedRef = useRef(false);

  // Fire activity_viewed exactly once, the first time the card is on screen. This
  // is the ONLY client-fired funnel event in the app.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // No observer (old/SSR) — count it as viewed on mount to avoid losing the signal.
      if (!viewedRef.current) {
        viewedRef.current = true;
        track('activity_viewed', {
          groupId,
          memberId,
          activityId: activity.id,
          offerType: activity.offerType,
          cloEligible: activity.cloEligible,
        });
      }
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            track('activity_viewed', {
              groupId,
              memberId,
              activityId: activity.id,
              offerType: activity.offerType,
              cloEligible: activity.cloEligible,
            });
            obs.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [activity.id, activity.offerType, activity.cloEligible, groupId, memberId]);

  const heading = useMemo(() => activity.title, [activity.title]);

  return (
    <li ref={ref}>
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>{heading}</CardTitle>
            <p className="mt-0.5 text-sm capitalize text-ink-700/70">
              {activity.category}
              {activity.area ? ` · ${activity.area}` : ''}
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-sand-100 px-3 py-1 text-sm font-semibold text-ink-800">
            {formatPrice(activity.priceFromPence)}
          </span>
        </div>

        <div className="mt-3">
          <OfferTag offerType={activity.offerType} cloEligible={activity.cloEligible} />
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {!shortlisted && (
            <Button variant="secondary" busy={busy} disabled={anyBusy && !busy} onClick={onShortlist}>
              Shortlist
            </Button>
          )}

          {shortlisted && !committed && (
            <div className="flex flex-col gap-3 rounded-xl2 bg-sand-50/60 p-3 ring-1 ring-sand-200">
              <p className="text-sm font-medium text-ink-800">Shortlisted. How many are coming?</p>
              <div className="flex items-center gap-3">
                <label htmlFor={`heads-${activity.id}`} className="text-sm text-ink-700">
                  Estimated heads
                </label>
                <input
                  id={`heads-${activity.id}`}
                  type="number"
                  min={1}
                  value={heads}
                  onChange={(e) => onHeads(Math.max(1, Number(e.target.value) || 1))}
                  className="w-20 rounded-xl2 border-0 bg-white px-3 py-2 text-ink-900 ring-1 ring-inset ring-sand-200 focus:ring-2 focus:ring-inset focus:ring-coral-500"
                />
              </div>
              <Button busy={busy} disabled={anyBusy && !busy} onClick={onCommit}>
                We’re in
              </Button>
            </div>
          )}

          {committed && (
            <div className="flex flex-col gap-3 rounded-xl2 bg-coral-400/10 p-3 ring-1 ring-coral-400/30">
              <p className="text-sm font-medium text-coral-700">
                Locked in for {heads} {heads === 1 ? 'person' : 'people'}. Ready to book?
              </p>
              <Button busy={busy} disabled={anyBusy && !busy} onClick={onClickOut}>
                Book / view details
              </Button>
            </div>
          )}
        </div>
      </Card>
    </li>
  );
}
